import { PopoverPlacement } from "@idui/react-popover";
import { AddCircleOutlined } from "@mui/icons-material";
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  IconButton,
  TextField,
} from "@mui/material";
import _ from "lodash";
import { useEffect, useMemo, type FC, type SyntheticEvent } from "react";
import { useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { useDynamicFormsTranslate } from "@ryde/redux";
import {
  bulkMultiselectActionsSelectItems,
  checkCleanupNestedValidation,
  dropdownsIsFetchingAtom,
  getBulkFormFieldName,
  getBulkMultiselectValueFormValidation,
  Log,
  normalizeToArrayValue,
  requiredRuleStringField,
  type ICustomField,
} from "@ryde/shared";

import { AccessControl, FramerTooltip } from "../../../common";
import { BulkActionFieldStackStyled } from "../bulk-action-field-stack.styled";
import { DropdownTemplate } from "../dropdown.template";
import { ListBox, useDropdownTemplate } from "../dropdowns";
import { SelectAllPaperWrapper } from "../selectall-paper-wrapper.template";
import { useClearBulkMultiselectOnAction } from "../use-clear-bulk-multiselect-on-action";
import { useIsBulkControlSelected } from "../use-is-bulk-control-selected";
import { BulkMultiselectBoundTemplateStyled } from "./bulk-multiselect-bound.styled";

export const BulkMultiselectBoundTemplate: FC<ICustomField> = ({
  id,
  name,
  formAttributes,
  formLabel,
  formValidation,
  e2eData = "bulk-multiselect-bound-template",
  labelTranslated,
}) => {
  const {
    control,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();
  const isBulkControlSelected = useIsBulkControlSelected(name);
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
    translatedText: labelTranslated,
  });
  const renderValue: number[] = getValues(name);
  const loadingItems = useRecoilValue(dropdownsIsFetchingAtom);
  useEffect(() => {
    if (renderValue && !_.isArray(renderValue)) {
      /* guard for invalid value coming from backend */
      Log.warn(
        t("errors.multiSelectBoundTemplateInitialValueNotArray"),
        renderValue,
      );
      // Autocomplete multiple cannot take a scalar; keep a single id as a one-item selection
      setValue(name, normalizeToArrayValue(renderValue));
    }
  }, [name, renderValue, setValue, t]);

  const bulkAction = watch(getBulkFormFieldName(name));
  useClearBulkMultiselectOnAction(name);
  const multiselectFormValidation = getBulkMultiselectValueFormValidation(
    isBulkControlSelected,
    bulkAction,
  );

  const {
    isAddButtonVisible,
    requiredComputed,
    isDisabled,
    dropdownOptions,
    handleInsertItem,
    selectAll,
    getOptionLabel,
    setInputText,
    inputText,
  } = useDropdownTemplate(
    "multi",
    name,
    formAttributes,
    multiselectFormValidation,
  );
  const {
    field: { value: formValue, ...restField },
    fieldState: { error },
  } = useController({ name, control });

  const { dropItemsSource, disableSorting } = formAttributes || {};

  const sortedValue = useMemo(() => {
    const currentSelection = normalizeToArrayValue<string | number>(formValue);
    if (disableSorting) {
      return currentSelection;
    }
    return currentSelection.toSorted((a, b) => {
      const labelA = getOptionLabel(a).toLowerCase();
      const labelB = getOptionLabel(b).toLowerCase();
      return labelA.localeCompare(labelB);
    });
  }, [formValue, disableSorting, getOptionLabel]);

  const value = useMemo(() => sortedValue, [sortedValue]);

  const onChangeDropdown = (
    e: SyntheticEvent<Element, Event>,
    newValue: readonly (string | number)[],
  ): void => {
    Log.debug("Dropdown multi template value changed to:", newValue);
    setValue(name, newValue, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <BulkMultiselectBoundTemplateStyled>
      <FormControl
        data-e2e-template="bulk-multiselect-bound-template"
        data-e2e-field-name={name}
        required={requiredComputed}
        fullWidth
        error={!!error}
      >
        <BulkActionFieldStackStyled>
          <DropdownTemplate
            name={getBulkFormFieldName(name)}
            formAttributes={{
              selectItems: bulkMultiselectActionsSelectItems,
            }}
            formValidation={
              isBulkControlSelected ? requiredRuleStringField : undefined
            }
            labelTranslated={t("general.multiselectBulkAction")}
          />
          <Autocomplete
            {...restField}
            data-e2e={e2eData}
            data-e2e-id={`${name}_${id || ""}`}
            disabled={isDisabled || loadingItems}
            multiple
            freeSolo
            options={dropdownOptions || []}
            value={value}
            slotProps={{
              listbox: {
                component: ListBox,
              },
            }}
            filterOptions={options => options}
            onChange={(event, newValue) => {
              if (!newValue) {
                return;
              }

              checkCleanupNestedValidation(name, errors);

              onChangeDropdown(event, newValue as (string | number)[]);
            }}
            inputValue={inputText}
            onInputChange={(event, newValue) => {
              setInputText(newValue ?? "");
            }}
            isOptionEqualToValue={(opt, val) => String(opt) === String(val)}
            getOptionLabel={getOptionLabel}
            disableCloseOnSelect
            renderInput={params => (
              <TextField
                {...params}
                error={!!error}
                disabled={isDisabled || loadingItems}
                label={formLabelTranslated}
                required={requiredComputed}
                data-e2e="multiselect-bound-template-input"
              />
            )}
            slots={{
              paper: props => (
                <SelectAllPaperWrapper
                  {...props}
                  selectAll={formValue?.length === dropdownOptions?.length}
                  handleToggleSelectAll={() => {
                    selectAll();
                  }}
                />
              ),
            }}
          />
        </BulkActionFieldStackStyled>
        {error && (
          <FormHelperText data-e2e="multiselect-bound-template-error">
            {error.message}
          </FormHelperText>
        )}
      </FormControl>
      <div className="bulk-multiselect-bound-template__access-control-wrapper">
        {isAddButtonVisible && (
          <AccessControl entity={dropItemsSource || "unknown"} action="create">
            <FramerTooltip
              content={t("general.ADD_NEW_DROPDOWN_ITEM_TOOLTIP")}
              placement={PopoverPlacement.top}
            >
              <IconButton onClick={handleInsertItem}>
                <AddCircleOutlined />
              </IconButton>
            </FramerTooltip>
          </AccessControl>
        )}
      </div>
    </BulkMultiselectBoundTemplateStyled>
  );
};
